// Story 9.6 (ADO-1080) — the "Previous orders" Filters panel (AC1-AC5).
//
// Figma "Previous Orders · 1440 · Clear in title row" 4925:50646, which is the latest
// of the delivered frames and the one that places the applied-filter count and the
// Clear action in the panel head. Four controls in one row: From date, To date, Order
// reference, and a Sales-rep-orders switch.
//
// ADO-1779: on mobile the same four controls live in a bottom sheet behind a "Filters"
// button (Figma 5415:21883), see `FiltersSheet` below; tablet keeps the panel, two
// controls to a row (5412:60442).
//
// ── This panel owns no filtering ──
// It renders values and reports changes. The filter STATE lives in the URL (AC8, see
// `OrderHistory.tsx`) and the filtering itself happens server-side (AC7). That split is
// why the panel is a controlled component with a single `onChange`: there is exactly one
// place a filter set can be assembled, so the URL, the request and the panel can never
// describe three different things.
//
// ── The two pickers constrain each other (AC9, the UI half) ──
// The From picker's `max` is the chosen To and the To picker's `min` is the chosen
// From, so an impossible range cannot be entered at all — the invalid days are visibly
// present but not selectable. The server still treats `from > to` as an empty window,
// because a URL can be typed by hand; the two halves are belt and braces, and AC9 asks
// for both.
//
// ── The order-reference field is debounced ──
// It is the only free-text control here, and every filter change pushes a history entry
// (AC8 requires browser back/forward to restore filters, which `replace` would break).
// Committing per keystroke would therefore put one entry, and one request, behind every
// letter. The field holds a draft and commits once the typing stops.

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Button, DateField, DatePicker, FormField, Icon, Modal, Toggle } from '../../design-system';
import styles from './OrderFilters.module.css';

/**
 * One complete filter set, as the screen and the URL both hold it. Days are ISO
 * `YYYY-MM-DD` strings (never `Date`s — see `design-system/DatePicker/calendar.ts`),
 * and `null`/`''`/`false` are each the "not filtering on this" value for their field.
 */
export interface OrderFilterValues {
  readonly submittedFrom: string | null;
  readonly submittedTo: string | null;
  readonly orderRef: string;
  readonly salesRepOnly: boolean;
}

/** The empty set — what Clear restores (AC5) and what an unfiltered URL parses to. */
export const NO_ORDER_FILTERS: OrderFilterValues = {
  submittedFrom: null,
  submittedTo: null,
  orderRef: '',
  salesRepOnly: false,
};

/**
 * How many of the four dimensions are actually narrowing the list — the count AC4 asks
 * the panel head to show. A blank reference and an off toggle count for nothing, which
 * is what makes "0 applied" and "the unfiltered list" the same statement.
 */
export function appliedFilterCount(values: OrderFilterValues): number {
  return [
    values.submittedFrom !== null && values.submittedFrom !== '',
    values.submittedTo !== null && values.submittedTo !== '',
    values.orderRef.trim() !== '',
    values.salesRepOnly,
  ].filter(Boolean).length;
}

/** How long the order-reference field waits after the last keystroke before committing. */
const ORDER_REF_COMMIT_DELAY_MS = 350;

/** The date triggers' ids, so the sheet can hand focus back to the field it opened from. */
const FROM_TRIGGER_ID = 'order-filters-from';
const TO_TRIGGER_ID = 'order-filters-to';

/** Which date bound the mobile sheet is picking, or null while it shows the fields. */
type CalendarStep = 'submittedFrom' | 'submittedTo' | null;
type DateBound = Exclude<CalendarStep, null>;

const CALENDAR_LABELS: Readonly<Record<DateBound, string>> = {
  submittedFrom: 'From date',
  submittedTo: 'To date',
};

export interface OrderFiltersProps {
  readonly values: OrderFilterValues;
  readonly onChange: (next: OrderFilterValues) => void;
  /** AC5 — reset every filter. Separate from `onChange(NO_ORDER_FILTERS)` so the caller
   *  can also reset the page in one step rather than inferring it. */
  readonly onClear: () => void;
  /** The viewer's today, for the calendar's ring. Injected so it is stable across
   *  renders and pinnable in a spec. */
  readonly today: string;
  /**
   * ADO-1779. `panel` is the card above the list (desktop and tablet). `sheet` is the
   * mobile composition (Figma 5415:21883): a "Filters" button, counting what is applied,
   * that opens the same four controls in a bottom sheet closed by "Show results". The
   * controls commit exactly as the panel's do, so the list behind the sheet is already
   * the result when it closes. The sheet has no Clear of its own: on mobile that action
   * sits over the list (Figma 5415:62725), which the screen renders.
   */
  readonly presentation?: 'panel' | 'sheet';
}

export function OrderFilters({
  values,
  onChange,
  onClear,
  today,
  presentation = 'panel',
}: OrderFiltersProps) {
  const applied = appliedFilterCount(values);

  // The draft the order-reference input actually displays. Kept local so typing is not
  // gated on a round trip through the URL and back.
  const [orderRefDraft, setOrderRefDraft] = useState(values.orderRef);
  /**
   * The last value this field COMMITTED. Without it, the effect below cannot tell
   * "the URL changed because I committed" (leave the draft alone, the user may have
   * typed on) from "the URL changed underneath me" — Clear, or browser back — where the
   * draft must be replaced. Comparing against `values.orderRef` alone would fight the
   * user's own typing.
   */
  const committedRef = useRef(values.orderRef);

  /**
   * The current props, readable from inside the debounce timer. The timer must depend
   * on the DRAFT alone: `values` and `onChange` are fresh objects on every parent
   * render, so depending on them would restart the countdown each time the parent
   * re-rendered for any reason at all and the field could sit there never committing.
   * Refreshed in an effect rather than during render, so nothing is mutated mid-render.
   */
  const latestRef = useRef({ values, onChange });
  useEffect(() => {
    latestRef.current = { values, onChange };
  });

  useEffect(() => {
    if (values.orderRef === committedRef.current) return;
    // An external change (Clear, back/forward, a pasted URL): adopt it.
    committedRef.current = values.orderRef;
    setOrderRefDraft(values.orderRef);
  }, [values.orderRef]);

  useEffect(() => {
    if (orderRefDraft === committedRef.current) return;
    const timer = setTimeout(() => {
      committedRef.current = orderRefDraft;
      const { values: latestValues, onChange: commit } = latestRef.current;
      commit({ ...latestValues, orderRef: orderRefDraft });
    }, ORDER_REF_COMMIT_DELAY_MS);
    // Cleared on every keystroke (so only the pause commits) and on unmount (so a
    // depot switch mid-typing cannot commit into a screen that has gone).
    return () => {
      clearTimeout(timer);
    };
  }, [orderRefDraft]);

  /** Commit the draft now, skipping the countdown (Enter, or leaving the field). */
  const commitOrderRefNow = () => {
    if (orderRefDraft === committedRef.current) return;
    committedRef.current = orderRefDraft;
    onChange({ ...values, orderRef: orderRefDraft });
  };

  /**
   * The four controls, shared by both presentations. `onOpenCalendar` is passed only by
   * the sheet, whose date fields hand their calendar to the sheet's own calendar step
   * rather than opening it under the field.
   */
  const renderFields = (onOpenCalendar?: (bound: DateBound) => void) => (
    <div className={styles.fields}>
      <DateField
        label="From date"
        id={FROM_TRIGGER_ID}
        value={values.submittedFrom}
        today={today}
        // AC9 UI half: cannot be moved past the chosen To.
        {...(values.submittedTo === null ? {} : { max: values.submittedTo })}
        rangeStart={values.submittedFrom}
        rangeEnd={values.submittedTo}
        onChange={(day) => onChange({ ...values, submittedFrom: day })}
        {...(onOpenCalendar === undefined
          ? {}
          : { onOpenCalendar: () => onOpenCalendar('submittedFrom') })}
      />
      <DateField
        label="To date"
        id={TO_TRIGGER_ID}
        value={values.submittedTo}
        today={today}
        // AC9 UI half: cannot be moved before the chosen From.
        {...(values.submittedFrom === null ? {} : { min: values.submittedFrom })}
        rangeStart={values.submittedFrom}
        rangeEnd={values.submittedTo}
        onChange={(day) => onChange({ ...values, submittedTo: day })}
        {...(onOpenCalendar === undefined
          ? {}
          : { onOpenCalendar: () => onOpenCalendar('submittedTo') })}
      />

      {/* Wrapped, because `FormField` styles its own label+input stack and this
          wrapper is what carries the row's 1fr share. */}
      <div className={styles.textField}>
        <FormField
          label="Order reference"
          placeholder="e.g. 1042 or ORD-2026"
          value={orderRefDraft}
          // `search`, not `text`: announced as a search field, which is what it is.
          type="search"
          autoComplete="off"
          onChange={(event) => setOrderRefDraft(event.target.value)}
          // Enter commits immediately rather than waiting out the countdown: pressing
          // it is the user saying they have finished typing. Blur does the same, so
          // tabbing straight to the toggle cannot lose the last thing typed.
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            commitOrderRefNow();
          }}
          onBlur={commitOrderRefNow}
        />
      </div>

      {/* A labelled group, not a bare stack: "Sales rep orders" names the field while
          "Show only" names the switch inside it, and a group is how that relationship
          is expressed to assistive tech. */}
      <div className={styles.repField} role="group" aria-labelledby="order-filters-sales-rep-label">
        <span className={styles.repLabel} id="order-filters-sales-rep-label">
          Sales rep orders
        </span>
        <div className={styles.repToggleRow}>
          <Toggle
            label="Show only"
            checked={values.salesRepOnly}
            onChange={(checked) => onChange({ ...values, salesRepOnly: checked })}
          />
        </div>
      </div>
    </div>
  );

  if (presentation === 'sheet') {
    return (
      <FiltersSheet
        values={values}
        onChange={onChange}
        today={today}
        applied={applied}
        renderFields={renderFields}
        onClose={commitOrderRefNow}
      />
    );
  }

  return (
    <section className={styles.panel} aria-labelledby="order-filters-heading">
      <div className={styles.head}>
        <h2 id="order-filters-heading" className={styles.heading}>
          Filters
        </h2>
        {/* AC4. Hidden at zero rather than reading "· 0 applied": the absence of the
            line is the clearer statement that nothing is filtered, and it matches the
            frame, which only ever draws the count with filters on. */}
        {applied > 0 ? <p className={styles.appliedCount}>· {applied} applied</p> : null}
        {/* AC5. Shown only when there is something to clear — a permanently-present
            Clear that does nothing is a control that lies about its own state. */}
        {applied > 0 ? (
          <button type="button" className={styles.clear} onClick={onClear}>
            <Icon name="arrowsClockwise" size={14} className={styles.clearIcon} />
            Clear filters
          </button>
        ) : null}
      </div>

      {renderFields()}
    </section>
  );
}

/**
 * The mobile Filters sheet (Figma 5415:21883, 5415:22192, 5415:22571). Two steps in
 * ONE dialog: the fields, closed by "Show results", and a date bound's calendar, titled
 * with that field's label (5415:22466). Picking a day, the close button, Escape or a
 * scrim press on the calendar step all return to the fields, with focus back on the
 * date field it was opened from; the same actions on the fields step close the sheet.
 * One dialog rather than a second sheet stacked over the first, because the frame
 * draws a single sheet whose content changes.
 */
function FiltersSheet({
  values,
  onChange,
  today,
  applied,
  renderFields,
  onClose,
}: {
  values: OrderFilterValues;
  onChange: (next: OrderFilterValues) => void;
  today: string;
  applied: number;
  renderFields: (onOpenCalendar: (bound: DateBound) => void) => ReactElement;
  /** Commits a pending order-reference draft as the sheet closes. */
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<CalendarStep>(null);
  /** The date field to refocus once the calendar step hands back to the fields. */
  const returnFocusRef = useRef<string | null>(null);

  // The field that held focus unmounts with each step change, so focus is placed by
  // hand: into the calendar's roving day on the way in, back to the date field it was
  // opened from on the way out.
  const calendarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (step !== null) {
      calendarRef.current?.querySelector<HTMLElement>('[data-focused="true"]')?.focus();
      return;
    }
    if (returnFocusRef.current === null) return;
    document.getElementById(returnFocusRef.current)?.focus();
    returnFocusRef.current = null;
  }, [step]);

  const backToFields = () => {
    returnFocusRef.current = step === 'submittedTo' ? TO_TRIGGER_ID : FROM_TRIGGER_ID;
    setStep(null);
  };

  const close = () => {
    onClose();
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="tertiary"
        fullWidth
        className={styles.sheetTrigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {/* AC4 on mobile: the count rides on the button (Figma 5415:62737). */}
        {applied > 0 ? `Filters · ${applied}` : 'Filters'}
      </Button>

      <Modal
        open={open}
        onClose={step === null ? close : backToFields}
        title={step === null ? 'Filters' : CALENDAR_LABELS[step]}
        placement="bottom"
        sheetGutter="compact"
        {...(step === null
          ? {
              footer: (
                <Button variant="primary" onClick={close}>
                  Show results
                </Button>
              ),
            }
          : {})}
      >
        {step === null ? (
          renderFields(setStep)
        ) : (
          <div ref={calendarRef}>
            <DatePicker
              variant="inline"
              aria-label={CALENDAR_LABELS[step]}
              value={values[step]}
              today={today}
              rangeStart={values.submittedFrom}
              rangeEnd={values.submittedTo}
              // AC9 UI half, as in the panel: each bound is capped by the other.
              {...(step === 'submittedFrom' && values.submittedTo !== null
                ? { max: values.submittedTo }
                : {})}
              {...(step === 'submittedTo' && values.submittedFrom !== null
                ? { min: values.submittedFrom }
                : {})}
              onSelect={(day) => {
                onChange({ ...values, [step]: day });
                backToFields();
              }}
            />
          </div>
        )}
      </Modal>
    </>
  );
}
